import { Link } from "react-router-dom";
import { MonitorSmartphone, ShoppingBag, LayoutDashboard, Cpu, ArrowRight, Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SERVICES } from "../../data/site";

const ICONS = { MonitorSmartphone, ShoppingBag, LayoutDashboard, Cpu };

export const ServiceCards = ({ detailed = false }) => (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-6">
        {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
                <Reveal key={s.num} delay={i * 0.08}>
                    <article
                        data-testid={`service-card-${s.num}`}
                        className="group relative h-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-card transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-hover sm:p-9"
                    >
                        <div
                            className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-brand/[0.07] blur-3xl opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                            aria-hidden="true"
                        />
                        <div className="flex items-start justify-between">
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white shadow-card transition-colors duration-500 group-hover:bg-brand-dark">
                                <Icon className="h-5 w-5" strokeWidth={1.8} />
                            </div>
                            <span className="font-mono text-xs font-semibold tracking-[0.25em] text-brand/40 transition-colors duration-500 group-hover:text-brand">
                                {s.num}
                            </span>
                        </div>
                        <h3 className="mt-8 font-display text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                            {s.title}
                        </h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                            {detailed ? s.long : s.desc}
                        </p>
                        {detailed && (
                            <ul className="mt-6 space-y-2.5">
                                {s.deliverables.map((d) => (
                                    <li key={d} className="flex items-start gap-2.5 text-sm text-slate-600">
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" strokeWidth={2.4} aria-hidden="true" />
                                        {d}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="mt-7 flex flex-wrap gap-2">
                            {s.tags.map((t) => (
                                <span
                                    key={t}
                                    className="rounded-full border border-brand/20 bg-brand-subtle px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-brand-dark"
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                    </article>
                </Reveal>
            );
        })}
    </div>
);

export default function Services() {
    return (
        <section data-testid="services-section" className="relative bg-paper py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <Reveal className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <Eyebrow testId="services-eyebrow">What we do</Eyebrow>
                        <h2 className="max-w-3xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                            From idea to launch.
                        </h2>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                            We combine engineering, design and product thinking to create digital
                            experiences that are fast, scalable and built around real business goals.
                        </p>
                    </div>
                    <Link
                        to="/services"
                        data-testid="services-view-all-link"
                        className="group flex items-center gap-2 border-b-2 border-brand/30 pb-1.5 text-sm font-semibold text-brand transition-[border-color,color] duration-300 hover:border-brand-dark hover:text-brand-dark"
                    >
                        All services
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
                <div className="mt-16">
                    <ServiceCards detailed />
                </div>
            </div>
        </section>
    );
}
